/**
 * Homing torpedoes (spec §6): a fixed SoA pool (not ECS entities, D-013/D-014), launch gated by the torpedo
 * lock, lead pursuit with a capped turn rate at constant speed, and detonation by proximity, raycast hit or
 * timeout. Positions are float64 world (D-011); every step function is allocation-free (D-014).
 */
import { rotateVec, type Quat, type Vec3 } from '../core/math'
import type { ShipDef, TorpedoWeaponDef, WeaponsData } from '../data/schemas'
import { createRay, createRayHit, setRay, type RaycastFn } from '../physics/physics'
import { Control, Hull, Life, Lock, Pos64, Rot, Ship, Target, Team, Vel, Weapons } from '../world/components'
import { applyDamage, createDamageResult } from './damage'
import { emitEvent, EV, type EventRing } from './events'
import { isHostile, notePlayerDamage } from './hostility'
import { createLeadSolution, solveLead } from './lead'
import { isLivingEnemy, type ShipList } from './targeting'

/** Default pool capacity (weapons.json `pools.torpedoes` overrides it). */
export const TORPEDO_CAPACITY = 64
/** f32 slack for cooldown and lifetime timers so 1.5 s and 12 s land on whole 60 Hz ticks. */
const TIMER_EPS = 1e-3

/** Structure-of-arrays torpedo pool. Slot `i` is live while `alive[i] === 1`. */
export interface TorpedoPool {
  capacity: number
  /** Position now (float64 world). */
  x: Float64Array
  y: Float64Array
  z: Float64Array
  /** Position at the start of the last step (render interpolation and the hit-test segment). */
  px: Float64Array
  py: Float64Array
  pz: Float64Array
  vx: Float32Array
  vy: Float32Array
  vz: Float32Array
  /** Target entity, -1 once lost (the torpedo then flies straight until its timer ends). */
  target: Int32Array
  owner: Int32Array
  team: Uint8Array
  /** Index into `WeaponsData.weapons`. */
  weapon: Uint8Array
  /** Seconds of life left. */
  ttl: Float32Array
  alive: Uint8Array
  /** Free-slot stack (`free[0..freeCount)`), popped from the top: slots are handed out 0, 1, 2… deterministically. */
  free: Int32Array
  freeCount: number
  /** Live torpedo count. */
  live: number
  /** Launches refused because the pool was full (perf overlay). */
  dropped: number
}

export function createTorpedoPool(capacity: number = TORPEDO_CAPACITY): TorpedoPool {
  const pool: TorpedoPool = {
    capacity,
    x: new Float64Array(capacity),
    y: new Float64Array(capacity),
    z: new Float64Array(capacity),
    px: new Float64Array(capacity),
    py: new Float64Array(capacity),
    pz: new Float64Array(capacity),
    vx: new Float32Array(capacity),
    vy: new Float32Array(capacity),
    vz: new Float32Array(capacity),
    target: new Int32Array(capacity).fill(-1),
    owner: new Int32Array(capacity).fill(-1),
    team: new Uint8Array(capacity),
    weapon: new Uint8Array(capacity),
    ttl: new Float32Array(capacity),
    alive: new Uint8Array(capacity),
    free: new Int32Array(capacity),
    freeCount: 0,
    live: 0,
    dropped: 0,
  }
  clearTorpedoes(pool)
  return pool
}

/** Frees every slot (sim reset). */
export function clearTorpedoes(pool: TorpedoPool): void {
  const n = pool.capacity
  for (let i = 0; i < n; i++) {
    pool.alive[i] = 0
    pool.target[i] = -1
    pool.free[i] = n - 1 - i
  }
  pool.freeCount = n
  pool.live = 0
}

/** Takes a slot and fills it. Returns the slot, or -1 (counted in `dropped`) when the pool is full. */
export function spawnTorpedo(
  pool: TorpedoPool,
  x: number,
  y: number,
  z: number,
  vx: number,
  vy: number,
  vz: number,
  target: number,
  owner: number,
  team: number,
  weapon: number,
  ttl: number,
): number {
  if (pool.freeCount === 0) {
    pool.dropped++
    return -1
  }
  const i = pool.free[--pool.freeCount]
  pool.x[i] = pool.px[i] = x
  pool.y[i] = pool.py[i] = y
  pool.z[i] = pool.pz[i] = z
  pool.vx[i] = vx
  pool.vy[i] = vy
  pool.vz[i] = vz
  pool.target[i] = target
  pool.owner[i] = owner
  pool.team[i] = team
  pool.weapon[i] = weapon
  pool.ttl[i] = ttl
  pool.alive[i] = 1
  pool.live++
  return i
}

/** Returns slot `i` to the pool (no-op when already free). */
export function freeTorpedo(pool: TorpedoPool, i: number): void {
  if (pool.alive[i] === 0) return
  pool.alive[i] = 0
  pool.target[i] = -1
  pool.free[pool.freeCount++] = i
  pool.live--
}

/** Drops `eid` as a pursuit target (call when a ship is despawned, so a recycled entity id is never chased). */
export function releaseTorpedoTarget(pool: TorpedoPool, eid: number): void {
  for (let i = 0; i < pool.capacity; i++) if (pool.target[i] === eid) pool.target[i] = -1
}

// ---------------------------------------------------------------------------------------------------------
// Launch

const q: Quat = { x: 0, y: 0, z: 0, w: 1 }
const v3: Vec3 = { x: 0, y: 0, z: 0 }

/** Index into `def.mounts` of the ship's first secondary mount, or -1. */
function secondaryMount(def: ShipDef): number {
  const mounts = def.mounts
  for (let i = 0; i < mounts.length; i++) if (mounts[i].group === 'secondary') return i
  return -1
}

/**
 * Secondary fire (torpedoes) for every ship. Counts `Weapons.cooldownSecondary` down, then launches when
 * `Control.fireSecondary` is held, the ship is alive, `Lock.locked` is set on the current `Target`, a round is
 * left and the cooldown (1 / rate) has elapsed (spec §6: fire only once locked). The torpedo leaves the
 * secondary mount's muzzle (HP_TORP_V on the Kestrel) with the ship's velocity + `speed` along the muzzle
 * direction; one round is spent and `torpedoLaunch` is emitted (x/y/z muzzle, d* direction, eid shooter,
 * other target, weapon).
 */
export function stepSecondaryWeapons(
  ships: ShipList,
  defs: readonly ShipDef[],
  weapons: WeaponsData,
  pool: TorpedoPool,
  events: EventRing,
  dt: number,
): void {
  for (let s = 0; s < ships.length; s++) {
    const eid = ships[s]
    const cd = Weapons.cooldownSecondary[eid] - dt
    Weapons.cooldownSecondary[eid] = cd > 0 ? cd : 0
    if (Control.fireSecondary[eid] !== 1 || Life.alive[eid] !== 1) continue
    if (cd > TIMER_EPS || Weapons.torpedoes[eid] === 0) continue
    const target = Lock.target[eid]
    if (Lock.locked[eid] !== 1 || target !== Target.eid[eid] || !isLivingEnemy(eid, target)) continue

    const def = defs[Ship.defIndex[eid]]
    const m = secondaryMount(def)
    if (m < 0) continue
    const mount = def.mounts[m]
    const w = weapons.weapons[mount.weapon]
    if (w.kind !== 'torpedo' || mount.muzzles.length === 0) continue
    const muzzle = mount.muzzles[Weapons.torpedoes[eid] % mount.muzzles.length]

    q.x = Rot.x[eid]
    q.y = Rot.y[eid]
    q.z = Rot.z[eid]
    q.w = Rot.w[eid]
    rotateVec(q, muzzle.pos.x, muzzle.pos.y, muzzle.pos.z, v3)
    const mx = Pos64.x[eid] + v3.x
    const my = Pos64.y[eid] + v3.y
    const mz = Pos64.z[eid] + v3.z
    rotateVec(q, muzzle.dir.x, muzzle.dir.y, muzzle.dir.z, v3)
    const slot = spawnTorpedo(
      pool,
      mx,
      my,
      mz,
      Vel.x[eid] + v3.x * w.speed,
      Vel.y[eid] + v3.y * w.speed,
      Vel.z[eid] + v3.z * w.speed,
      target,
      eid,
      Team.id[eid],
      mount.weapon,
      w.lifeSeconds,
    )
    if (slot < 0) continue
    Weapons.torpedoes[eid]--
    Weapons.cooldownSecondary[eid] = 1 / w.rate
    emitEvent(events, EV.torpedoLaunch, mx, my, mz, v3.x, v3.y, v3.z, eid, target, mount.weapon, 0)
  }
}

// ---------------------------------------------------------------------------------------------------------
// Flight and detonation

const hit = createRayHit()
const ray = createRay()
const damage = createDamageResult()
const lead = createLeadSolution()

function contains(ships: ShipList, eid: number): boolean {
  for (let i = 0; i < ships.length; i++) if (ships[i] === eid) return true
  return false
}

/**
 * Applies the torpedo's damage to `victim` and emits the hit, shield-down and kill events. The hit point is
 * (x, y, z) with surface normal n.
 */
function damageShip(w: TorpedoWeaponDef, weapon: number, victim: number, owner: number, x: number, y: number, z: number, nx: number, ny: number, nz: number, events: EventRing, now: number): void {
  applyDamage(victim, w.damage, w.vsShield, w.vsHull, now, damage)
  if (damage.shieldAbsorbed > 0) emitEvent(events, EV.hitShield, x, y, z, nx, ny, nz, victim, owner, weapon, damage.shieldAbsorbed)
  if (damage.shieldDown) emitEvent(events, EV.shieldDown, Pos64.x[victim], Pos64.y[victim], Pos64.z[victim], 0, 0, 0, victim, owner, weapon, 0)
  if (damage.hullDamage > 0) emitEvent(events, EV.hitHull, x, y, z, nx, ny, nz, victim, owner, weapon, damage.hullDamage)
  if (damage.killed) emitEvent(events, EV.kill, Pos64.x[victim], Pos64.y[victim], Pos64.z[victim], 0, 0, 0, victim, owner, weapon, 0)
  if (damage.shieldAbsorbed > 0 || damage.hullDamage > 0) notePlayerDamage(owner, victim, now)
}

/** Detonates slot `i` at (x, y, z): `explode` (eid -1, other owner, amount = blast radius), then frees it. */
function detonate(pool: TorpedoPool, i: number, w: TorpedoWeaponDef, x: number, y: number, z: number, events: EventRing): void {
  pool.x[i] = x
  pool.y[i] = y
  pool.z[i] = z
  emitEvent(events, EV.explode, x, y, z, 0, 0, 0, -1, pool.owner[i], pool.weapon[i], w.proximity)
  freeTorpedo(pool, i)
}

/**
 * Advances every live torpedo by `dt`:
 * - Pursuit: lead pursuit toward the target's constant-velocity intercept (`solveLead` at the torpedo's speed),
 *   turning at most `turnRate × dt` per step, at a constant `speed`. A target that died, was despawned or is no
 *   longer hostile is dropped for good and the torpedo flies straight until its timer ends.
 * - Detonation, first along the step wins: entering the `proximity` sphere around the target (damage to the
 *   target), or a raycast hit (asteroid, derelict or any ship except the owner; damage to a hostile ship hit);
 *   otherwise at the end of `lifeSeconds`. Friendly fire is off (plan §2.5).
 */
export function stepTorpedoes(
  pool: TorpedoPool,
  ships: ShipList,
  weapons: WeaponsData,
  raycast: RaycastFn,
  events: EventRing,
  now: number,
  dt: number,
): void {
  for (let i = 0; i < pool.capacity; i++) {
    if (pool.alive[i] === 0) continue
    const wd = weapons.weapons[pool.weapon[i]]
    if (wd === undefined || wd.kind !== 'torpedo') {
      freeTorpedo(pool, i)
      continue
    }
    const w = wd
    const px = pool.x[i]
    const py = pool.y[i]
    const pz = pool.z[i]
    pool.px[i] = px
    pool.py[i] = py
    pool.pz[i] = pz

    // Current heading.
    let vx = pool.vx[i]
    let vy = pool.vy[i]
    let vz = pool.vz[i]
    let vl = Math.sqrt(vx * vx + vy * vy + vz * vz)

    let t = pool.target[i]
    if (t >= 0 && (Life.alive[t] !== 1 || Hull.value[t] <= 0 || !isHostile(pool.team[i], Team.id[t]) || !contains(ships, t))) {
      t = -1
      pool.target[i] = -1
    }

    if (t >= 0) {
      solveLead(px, py, pz, 0, 0, 0, Pos64.x[t], Pos64.y[t], Pos64.z[t], Vel.x[t], Vel.y[t], Vel.z[t], w.speed, lead)
      let ax = lead.x - px
      let ay = lead.y - py
      let az = lead.z - pz
      const al = Math.sqrt(ax * ax + ay * ay + az * az)
      if (al > 0) {
        ax /= al
        ay /= al
        az /= al
        if (vl === 0) {
          vx = ax
          vy = ay
          vz = az
          vl = 1
        }
        const hx = vx / vl
        const hy = vy / vl
        const hz = vz / vl
        const cos = hx * ax + hy * ay + hz * az
        const maxTurn = w.turnRate * dt
        if (cos >= Math.cos(maxTurn)) {
          vx = ax
          vy = ay
          vz = az
        } else {
          // Rotate the heading toward the aim direction by exactly maxTurn, in their common plane.
          let ux = ax - hx * cos
          let uy = ay - hy * cos
          let uz = az - hz * cos
          let ul = Math.sqrt(ux * ux + uy * uy + uz * uz)
          if (ul < 1e-9) {
            // Aim straight behind: turn about any axis perpendicular to the heading.
            ux = Math.abs(hx) < 0.9 ? 0 : -hz
            uy = Math.abs(hx) < 0.9 ? hz : 0
            uz = Math.abs(hx) < 0.9 ? -hy : hx
            ul = Math.sqrt(ux * ux + uy * uy + uz * uz)
          }
          const c = Math.cos(maxTurn)
          const sn = Math.sin(maxTurn) / ul
          vx = hx * c + ux * sn
          vy = hy * c + uy * sn
          vz = hz * c + uz * sn
        }
        vl = 1
      }
    }
    if (vl === 0) {
      // A torpedo launched from rest with no target: nothing to steer by; it just waits out its timer.
      vl = 1
    }
    // Unit heading × constant speed.
    const dx = vx / vl
    const dy = vy / vl
    const dz = vz / vl
    pool.vx[i] = dx * w.speed
    pool.vy[i] = dy * w.speed
    pool.vz[i] = dz * w.speed
    const len = w.speed * dt

    // Proximity: first entry into the target's sphere along this step (0 when already inside).
    let sProx = -1
    if (t >= 0) {
      const ox = px - Pos64.x[t]
      const oy = py - Pos64.y[t]
      const oz = pz - Pos64.z[t]
      const r = w.proximity
      const c = ox * ox + oy * oy + oz * oz - r * r
      if (c <= 0) {
        sProx = 0
      } else {
        const b = ox * dx + oy * dy + oz * dz
        const disc = b * b - c
        if (b < 0 && disc >= 0) {
          const s = -b - Math.sqrt(disc)
          if (s <= len) sProx = s
        }
      }
    }

    const rayHit = len > 0 && raycast(setRay(ray, px, py, pz, dx, dy, dz, len), pool.owner[i], hit)
    if (rayHit && (sProx < 0 || hit.toi < sProx)) {
      const victim = hit.eid
      if (victim >= 0 && Team.id[victim] !== pool.team[i]) {
        damageShip(w, pool.weapon[i], victim, pool.owner[i], hit.x, hit.y, hit.z, hit.nx, hit.ny, hit.nz, events, now)
      }
      detonate(pool, i, w, hit.x, hit.y, hit.z, events)
      continue
    }
    if (sProx >= 0) {
      const bx = px + dx * sProx
      const by = py + dy * sProx
      const bz = pz + dz * sProx
      let nx = bx - Pos64.x[t]
      let ny = by - Pos64.y[t]
      let nz = bz - Pos64.z[t]
      const nl = Math.sqrt(nx * nx + ny * ny + nz * nz)
      if (nl > 0) {
        nx /= nl
        ny /= nl
        nz /= nl
      } else {
        nx = -dx
        ny = -dy
        nz = -dz
      }
      damageShip(w, pool.weapon[i], t, pool.owner[i], bx, by, bz, nx, ny, nz, events, now)
      detonate(pool, i, w, bx, by, bz, events)
      continue
    }

    const nx = px + dx * len
    const ny = py + dy * len
    const nz = pz + dz * len
    const ttl = pool.ttl[i] - dt
    pool.ttl[i] = ttl
    if (ttl <= TIMER_EPS) {
      detonate(pool, i, w, nx, ny, nz, events)
      continue
    }
    pool.x[i] = nx
    pool.y[i] = ny
    pool.z[i] = nz
  }
}

